// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  ActionIcon,
  SimpleGrid,
  Tabs,
  Loader,
  Center,
  Badge,
  Alert,
  Divider,
  Title,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconCheck,
  IconX,
  IconSend,
  IconReportMoney,
  IconAlertTriangle,
  IconArrowLeft,
  IconReceipt,
} from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { CurrencyDisplay } from '../../components/CurrencyDisplay';
import { DataTable, type Column } from '../../components/DataTable';
import {
  useExpenseReports,
  useExpenseReport,
  useCreateExpenseReport,
  useSubmitExpenseReport,
  useApproveExpenseReport,
  useRejectExpenseReport,
  useAddExpenseToReport,
  useExpenses,
} from '../../hooks/useExpenses';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/format';
import type { Expense, ExpenseReport, CreateExpenseReportInput } from '../../types/expense';
import { EXPENSE_CATEGORY_LABELS, EXPENSE_CATEGORY_COLORS } from '../../types/expense';

const REPORT_STATUS_COLORS: Record<string, string> = {
  draft: 'gray',
  submitted: 'blue',
  approved: 'green',
  rejected: 'red',
  reimbursed: 'teal',
};

function ExpenseReportListView() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState<string | null>('my');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ExpenseReport | null>(null);

  // Create form
  const [title, setTitle] = useState('');
  const [period, setPeriod] = useState('');

  // Reject form
  const [rejectReason, setRejectReason] = useState('');

  const queryParams = {
    page,
    perPage: 25,
    status: activeTab === 'pending_approval' ? 'submitted' : undefined,
  };

  const { data: reportsData, loading } = useExpenseReports(queryParams);

  const createReport = useCreateExpenseReport();
  const submitReport = useSubmitExpenseReport();
  const approveReport = useApproveExpenseReport();
  const rejectReport = useRejectExpenseReport();

  const openCreateModal = () => {
    setTitle('');
    setPeriod('');
    setCreateModalOpen(true);
  };

  const handleCreate = async () => {
    if (!title.trim() || !period.trim()) {
      notifications.show({ title: 'Validation Error', message: 'Title and period are required.', color: 'red' });
      return;
    }

    try {
      const input: CreateExpenseReportInput = {
        employeeId: '', // Server assigns from auth context
        title,
        period,
      };
      await createReport.mutateAsync(input);
      notifications.show({ title: 'Report Created', message: 'Expense report has been created.', color: 'green' });
      setCreateModalOpen(false);
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to create report.', color: 'red' });
    }
  };

  const handleSubmit = async (report: ExpenseReport) => {
    if (report.expenses.length === 0) {
      notifications.show({ title: 'Cannot Submit', message: 'Add at least one expense to the report before submitting.', color: 'orange' });
      return;
    }
    try {
      await submitReport.mutateAsync({ id: report.id });
      notifications.show({ title: 'Report Submitted', message: 'Expense report submitted for approval.', color: 'blue' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to submit report.', color: 'red' });
    }
  };

  const handleApprove = async (report: ExpenseReport) => {
    try {
      await approveReport.mutateAsync({ id: report.id });
      notifications.show({ title: 'Report Approved', message: `Expense report "${report.title}" approved.`, color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to approve report.', color: 'red' });
    }
  };

  const handleReject = async () => {
    if (!selectedReport || !rejectReason.trim()) return;
    try {
      await rejectReport.mutateAsync({ id: selectedReport.id, reason: rejectReason });
      notifications.show({ title: 'Report Rejected', message: 'Expense report has been rejected.', color: 'orange' });
      setRejectModalOpen(false);
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to reject report.', color: 'red' });
    }
  };

  const columns: Column<ExpenseReport>[] = [
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      render: (r) => (
        <Text fw={600} size="sm">{r.title}</Text>
      ),
    },
    {
      key: 'employeeName',
      label: 'Employee',
      render: (r) => r.employeeName,
    },
    {
      key: 'period',
      label: 'Period',
      width: 140,
      render: (r) => r.period,
    },
    {
      key: 'expenses',
      label: 'Items',
      width: 80,
      render: (r) => String(r.expenses.length),
    },
    {
      key: 'totalAmount',
      label: 'Total',
      width: 120,
      render: (r) => <CurrencyDisplay amount={r.totalAmount} size="sm" />,
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (r) => <StatusBadge status={r.status} colorMap={REPORT_STATUS_COLORS} />,
    },
    {
      key: 'submittedAt',
      label: 'Submitted',
      width: 130,
      render: (r) => r.submittedAt ? formatDate(r.submittedAt) : '-',
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title="Expense Reports"
        breadcrumbs={[
          { label: 'Expenses', to: '/admin/workforce/expenses' },
          { label: 'Reports' },
        ]}
        subtitle={reportsData ? `${reportsData.length} reports` : undefined}
        actions={
          <Group gap="sm">
            <Button
              variant="subtle"
              icon={<IconArrowLeft size={16} />}
              onClick={() => navigate('/admin/workforce/expenses')}
            >
              Back to Expenses
            </Button>
            <Button
              icon={<IconPlus size={16} />}
              onClick={openCreateModal}
            >
              New Report
            </Button>
          </Group>
        }
      />

      <Tabs value={activeTab} onChange={(v) => { setActiveTab(v); setPage(1); }}>
        <Tabs.List>
          <Tabs.Tab value="my">My Reports</Tabs.Tab>
          <Tabs.Tab value="pending_approval">Pending Approval</Tabs.Tab>
          <Tabs.Tab value="all">All Reports</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {loading ? (
        <Center py="xl"><Loader /></Center>
      ) : !reportsData?.length ? (
        <EmptyState
          icon={<IconReportMoney size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
          title="No expense reports found"
          message="Group your expenses into reports for streamlined approval."
          action={{ label: 'Create Report', onClick: openCreateModal }}
        />
      ) : (
        <DataTable<ExpenseReport>
          columns={columns}
          data={reportsData}
          loading={loading}
          pagination={{
            page,
            pageSize: 25,
            total: reportsData.length,
            onPageChange: setPage,
          }}
          onRowClick={(r) => navigate(`/expenses/reports/${r.id}`)}
          getRowKey={(r) => r.id}
          emptyMessage="No expense reports found"
          actions={(r) => (
            <Group gap={4} wrap="nowrap">
              {r.status === 'draft' && (
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color="teal"
                  onClick={() => handleSubmit(r)}
                  title="Submit for Approval"
                >
                  <IconSend size={16} />
                </ActionIcon>
              )}
              {r.status === 'submitted' && activeTab === 'pending_approval' && (
                <>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color="green"
                    onClick={() => handleApprove(r)}
                    title="Approve"
                  >
                    <IconCheck size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color="red"
                    onClick={() => { setSelectedReport(r); setRejectReason(''); setRejectModalOpen(true); }}
                    title="Reject"
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </>
              )}
            </Group>
          )}
        />
      )}

      {/* Create Report Modal */}
      <Modal
        opened={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="New Expense Report"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Report Title"
            placeholder="e.g., January 2026 Business Travel"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            required
          />
          <TextInput
            label="Period"
            placeholder="e.g., Jan 2026 or Q1 2026"
            value={period}
            onChange={(e) => setPeriod(e.currentTarget.value)}
            required
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setCreateModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} loading={createReport.isPending}>
              Create Report
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Reject Report Modal */}
      <Modal
        opened={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Reject Expense Report"
        size="sm"
      >
        <Stack gap="md">
          {selectedReport && (
            <Group justify="space-between">
              <Text size="sm" fw={500}>{selectedReport.title}</Text>
              <CurrencyDisplay amount={selectedReport.totalAmount} size="sm" />
            </Group>
          )}

          <Textarea
            label="Reason for rejection"
            placeholder="Explain why this report is being rejected"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            rows={3}
            required
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setRejectModalOpen(false)}>Cancel</Button>
            <Button
              color="red"
              onClick={handleReject}
              loading={rejectReport.isPending}
              disabled={!rejectReason.trim()}
            >
              Reject Report
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function ExpenseReportDetailView() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();

  const { data: report, loading } = useExpenseReport(reportId ?? '');
  const { data: unassignedExpenses } = useExpenses({ status: 'draft' });

  const submitReport = useSubmitExpenseReport();
  const approveReport = useApproveExpenseReport();
  const rejectReportMut = useRejectExpenseReport();
  const addExpenseToReport = useAddExpenseToReport();

  const [addExpenseModalOpen, setAddExpenseModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  if (loading) {
    return <Center py="xl"><Loader /></Center>;
  }

  if (!report) {
    return <Center py="xl"><Text c="dimmed">Expense report not found.</Text></Center>;
  }

  const totalViolations = report.expenses.reduce(
    (count: number, exp: Expense) => count + exp.policyViolations.length,
    0,
  );

  const handleSubmit = async () => {
    if (report.expenses.length === 0) {
      notifications.show({ title: 'Cannot Submit', message: 'Add expenses before submitting.', color: 'orange' });
      return;
    }
    try {
      await submitReport.mutateAsync({ id: report.id });
      notifications.show({ title: 'Report Submitted', message: 'Expense report submitted for approval.', color: 'blue' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to submit.', color: 'red' });
    }
  };

  const handleApprove = async () => {
    try {
      await approveReport.mutateAsync({ id: report.id });
      notifications.show({ title: 'Report Approved', message: 'Expense report approved.', color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to approve.', color: 'red' });
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    try {
      await rejectReportMut.mutateAsync({ id: report.id, reason: rejectReason });
      notifications.show({ title: 'Report Rejected', message: 'Expense report rejected.', color: 'orange' });
      setRejectModalOpen(false);
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to reject.', color: 'red' });
    }
  };

  const handleAddExpense = async (expenseId: string) => {
    try {
      await addExpenseToReport.mutateAsync({ reportId: report.id, expenseId });
      notifications.show({ title: 'Expense Added', message: 'Expense added to report.', color: 'green' });
      setAddExpenseModalOpen(false);
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to add expense.', color: 'red' });
    }
  };

  const canSubmit = report.status === 'draft';
  const canApprove = report.status === 'submitted';
  const canAddExpenses = report.status === 'draft';

  const expenseColumns: Column<Expense>[] = [
    {
      key: 'date',
      label: 'Date',
      width: 110,
      render: (exp) => formatDate(exp.date),
    },
    {
      key: 'category',
      label: 'Category',
      width: 150,
      render: (exp) => (
        <Badge
          color={EXPENSE_CATEGORY_COLORS[exp.category] ?? 'gray'}
          variant="light"
          size="sm"
        >
          {EXPENSE_CATEGORY_LABELS[exp.category] ?? exp.category}
        </Badge>
      ),
    },
    {
      key: 'description',
      label: 'Description',
      render: (exp) => exp.description,
    },
    {
      key: 'vendor',
      label: 'Vendor',
      render: (exp) => exp.vendor,
    },
    {
      key: 'amount',
      label: 'Amount',
      width: 110,
      render: (exp) => <CurrencyDisplay amount={exp.amount} size="sm" />,
    },
    {
      key: 'policyViolations',
      label: 'Violations',
      width: 100,
      render: (exp) =>
        exp.policyViolations.length > 0 ? (
          <Badge color="orange" variant="light" size="sm">
            {exp.policyViolations.length}
          </Badge>
        ) : (
          <Text size="xs" c="dimmed">None</Text>
        ),
    },
  ];

  // Filter available expenses (those not yet assigned to a report)
  const availableExpenses = ((unassignedExpenses as any)?.data ?? []).filter(
    (exp: Expense) => !exp.reportId && exp.status === 'draft',
  );

  return (
    <Stack gap="md">
      <PageHeader
        title={report.title}
        breadcrumbs={[
          { label: 'Expenses', to: '/admin/workforce/expenses' },
          { label: 'Reports', to: '/admin/workforce/expenses/reports' },
          { label: report.title },
        ]}
        actions={
          <Group gap="sm">
            <Button
              variant="subtle"
              icon={<IconArrowLeft size={16} />}
              onClick={() => navigate('/admin/workforce/expenses/reports')}
            >
              Back
            </Button>
            {canAddExpenses && (
              <Button
                variant="outline"
                icon={<IconPlus size={16} />}
                onClick={() => setAddExpenseModalOpen(true)}
              >
                Add Expense
              </Button>
            )}
            {canSubmit && (
              <Button
                color="teal"
                icon={<IconSend size={16} />}
                onClick={handleSubmit}
                loading={submitReport.isPending}
              >
                Submit for Approval
              </Button>
            )}
            {canApprove && (
              <>
                <Button
                  color="green"
                  icon={<IconCheck size={16} />}
                  onClick={handleApprove}
                  loading={approveReport.isPending}
                >
                  Approve
                </Button>
                <Button
                  color="red"
                  variant="outline"
                  icon={<IconX size={16} />}
                  onClick={() => { setRejectReason(''); setRejectModalOpen(true); }}
                >
                  Reject
                </Button>
              </>
            )}
          </Group>
        }
      />

      {/* Report Header */}
      <Paper withBorder p="lg" radius="md">
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Employee</Text>
            <Text fw={600}>{report.employeeName}</Text>
          </Stack>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Period</Text>
            <Text fw={600}>{report.period}</Text>
          </Stack>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Status</Text>
            <Group gap="sm">
              <StatusBadge status={report.status} colorMap={REPORT_STATUS_COLORS} size="md" />
              {report.submittedAt && (
                <Text size="xs" c="dimmed">Submitted {formatDateTime(report.submittedAt)}</Text>
              )}
              {report.approvedAt && (
                <Text size="xs" c="dimmed">Approved {formatDateTime(report.approvedAt)}</Text>
              )}
            </Group>
          </Stack>
        </SimpleGrid>

        <Divider my="md" />

        <Group justify="space-between" ta="center">
          <Group gap="lg">
            <Stack gap={0}>
              <Text size="xs" c="dimmed">Expenses</Text>
              <Text fw={700} size="xl">{report.expenses.length}</Text>
            </Stack>
            {totalViolations > 0 && (
              <Stack gap={0}>
                <Text size="xs" c="dimmed">Policy Violations</Text>
                <Text fw={700} size="xl" c="orange">{totalViolations}</Text>
              </Stack>
            )}
          </Group>
          <Stack gap={0} ta="flex-end">
            <Text size="xs" c="dimmed">Total Amount</Text>
            <Text fw={700} size="xl">{formatCurrency(report.totalAmount)}</Text>
          </Stack>
        </Group>
      </Paper>

      {/* Policy Violation Warnings */}
      {totalViolations > 0 && (
        <Alert
          icon={<IconAlertTriangle size={20} />}
          title="Policy Violations Detected"
          color="orange"
          variant="light"
        >
          <Stack gap={4}>
            {report.expenses
              .filter((exp: Expense) => exp.policyViolations.length > 0)
              .map((exp: Expense) => (
                <Text key={exp.id} size="sm">
                  <Text span fw={600}>{exp.description}</Text>: {exp.policyViolations.join(', ')}
                </Text>
              ))}
          </Stack>
        </Alert>
      )}

      {/* Rejection Reason */}
      {report.rejectionReason && (
        <Alert
          icon={<IconX size={20} />}
          title="Report Rejected"
          color="red"
          variant="light"
        >
          <Text size="sm">{report.rejectionReason}</Text>
          {report.approvedBy && (
            <Text size="xs" c="dimmed" mt="xs">By: {report.approvedBy}</Text>
          )}
        </Alert>
      )}

      {/* Expense Line Items */}
      <Paper withBorder p="lg" radius="md">
        <Title order={4} mb="md">Expenses</Title>
        {report.expenses.length === 0 ? (
          <EmptyState
            icon={<IconReceipt size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
            title="No expenses in this report"
            message="Add expenses to this report before submitting for approval."
            action={canAddExpenses ? { label: 'Add Expense', onClick: () => setAddExpenseModalOpen(true) } : undefined}
          />
        ) : (
          <DataTable<Expense>
            columns={expenseColumns}
            data={report.expenses}
            getRowKey={(exp) => exp.id}
            emptyMessage="No expenses"
          />
        )}
      </Paper>

      {/* Add Expense to Report Modal */}
      <Modal
        opened={addExpenseModalOpen}
        onClose={() => setAddExpenseModalOpen(false)}
        title="Add Expense to Report"
        size="lg"
      >
        <Stack gap="md">
          {availableExpenses.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="md">
              No unassigned draft expenses available. Create new expenses first.
            </Text>
          ) : (
            <DataTable<Expense>
              columns={[
                { key: 'date', label: 'Date', width: 100, render: (e) => formatDate(e.date) },
                {
                  key: 'category',
                  label: 'Category',
                  width: 140,
                  render: (e) => (
                    <Badge
                      color={EXPENSE_CATEGORY_COLORS[e.category] ?? 'gray'}
                      variant="light"
                      size="sm"
                    >
                      {EXPENSE_CATEGORY_LABELS[e.category] ?? e.category}
                    </Badge>
                  ),
                },
                { key: 'description', label: 'Description', render: (e) => e.description },
                { key: 'vendor', label: 'Vendor', render: (e) => e.vendor },
                { key: 'amount', label: 'Amount', width: 100, render: (e) => <CurrencyDisplay amount={e.amount} size="sm" /> },
              ]}
              data={availableExpenses}
              getRowKey={(e) => e.id}
              emptyMessage="No available expenses"
              actions={(exp) => (
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => handleAddExpense(exp.id)}
                  loading={addExpenseToReport.isPending}
                >
                  Add
                </Button>
              )}
            />
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAddExpenseModalOpen(false)}>Close</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Reject Report Modal */}
      <Modal
        opened={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Reject Expense Report"
        size="sm"
      >
        <Stack gap="md">
          <Textarea
            label="Reason for rejection"
            placeholder="Explain why this report is being rejected"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            rows={3}
            required
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setRejectModalOpen(false)}>Cancel</Button>
            <Button
              color="red"
              onClick={handleReject}
              loading={rejectReportMut.isPending}
              disabled={!rejectReason.trim()}
            >
              Reject Report
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

export default function ExpenseReportPage() {
  const { reportId } = useParams<{ reportId: string }>();

  if (reportId) {
    return <ExpenseReportDetailView />;
  }

  return <ExpenseReportListView />;
}
